/**
 * 报名详情页的「发票」区块（口径 C-25：学员申请 → 财务人工开具 / 驳回）
 * -----------------------------------------------------------------------------
 * 单表生命周期在学员侧只有四种呈现，**互斥且穷尽**（服务端恒返回「最新一行」）：
 *
 *   未申请    → 可申请时给入口；不可申请时说明原因（不显示一个注定被拒的表单）
 *   REQUESTED → 待开具，说明「财务开具后可回来查发票号」
 *   ISSUED    → 已开具，展示发票号码与开具时间
 *   REJECTED  → 展示驳回原因 + **可重新申请**（驳回不是终态）
 *
 * 三条刻意的取舍：
 *   1. **界面不显示「开票金额」输入、也不自己算金额** —— 金额由服务端按缴费对账基准
 *      写入，前端连表达它的能力都没有（契约 `InvoiceApplyRequest` 里没有该字段）。
 *   2. **驳回原因必须原样展示** —— 学员要据此决定「改什么再提一次」。
 *   3. **本节的自查询失败不整页报错** —— 降级为一条可重试的提示。
 */
import { INVOICE_STATUS_LABEL, InvoiceStatus, InvoiceTitleType } from '@enroll/shared';
import { Input, Radio, Toast } from 'antd-mobile';
import { useState } from 'react';
import { describeApiError } from '../../api/errors';
import { applyInvoice } from '../../api/h5';
import { useMyInvoice } from '../../api/hooks';
import type { InvoiceRecordView } from '../../api/types';
import { formatDateTime } from '../../lib/datetime';
import { formatCentsText } from '../FormRenderer/logic';
import { Button, Card, Cell, KV, LoadingBlock, Notice } from '../layout';
import {
  INVOICE_TITLE_TYPE_OPTIONS,
  type InvoiceDraft,
  initialInvoiceDraft,
  invoiceDraftProblem,
  invoicePayloadOf,
} from './logic';

export interface InvoicePanelProps {
  registrationId: string;
  /** 是否可申请 —— 由 `isInvoiceableRegistrationStatus(报名单状态)` 判定（与服务端同源） */
  canApply: boolean;
  /** 报名人姓名：个人抬头的默认值 */
  applicantName?: string | null;
}

/** 抬头 / 税号 / 金额 / 号码的只读回显（待开具、已开具、已驳回三种状态共用） */
function InvoiceDetail({ record }: { record: InvoiceRecordView }) {
  return (
    <Card title="开票信息" tight>
      <KV label="状态" value={INVOICE_STATUS_LABEL[record.status]} />
      <KV
        label="抬头类型"
        value={record.titleType === InvoiceTitleType.COMPANY ? '单位' : '个人'}
      />
      <KV label="发票抬头" value={record.title} />
      {record.taxNo ? <KV label="纳税人识别号" value={record.taxNo} mono /> : null}
      <KV
        label="开票金额"
        value={<span className="mono-num">{formatCentsText(record.amountCents)}</span>}
      />
      {record.invoiceNo ? <KV label="发票号码" value={record.invoiceNo} mono /> : null}
      {record.issuedAt ? <KV label="开具时间" value={formatDateTime(record.issuedAt)} /> : null}
      {record.remark ? <KV label="申请备注" value={record.remark} /> : null}
    </Card>
  );
}

function InvoiceForm({
  registrationId,
  applicantName,
  onApplied,
  onCancel,
}: {
  registrationId: string;
  applicantName?: string | null;
  onApplied: () => Promise<void>;
  /** 驳回态下才需要「取消」（回到驳回原因）；未申请态不显示 */
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState<InvoiceDraft>(() => initialInvoiceDraft(applicantName));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = invoiceDraftProblem(draft);

  const patch = (next: Partial<InvoiceDraft>) => setDraft((prev) => ({ ...prev, ...next }));

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await applyInvoice(registrationId, invoicePayloadOf(draft));
      await onApplied();
      Toast.show({ content: '申请已提交，财务开具后可查看发票号码' });
    } catch (caught) {
      // 服务端 `INVOICE-001`（单位抬头缺税号）/ `INVOICE-002`（重复申请）/
      // `INVOICE-003`（报名状态不允许）都带可读文案，直接展示，不在本地另编一套
      setError(describeApiError(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card title="申请开票">
        <div className="field">
          <span className="field__label">抬头类型</span>
          <span className="field__control">
            <Radio.Group
              value={draft.titleType}
              onChange={(next) => {
                if (next !== InvoiceTitleType.PERSONAL && next !== InvoiceTitleType.COMPANY) return;
                patch({ titleType: next });
              }}
            >
              {INVOICE_TITLE_TYPE_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value} className="choice-row">
                  {option.label}
                </Radio>
              ))}
            </Radio.Group>
          </span>
        </div>

        <div className="field">
          <span className="field__label">发票抬头</span>
          <span className="field__control">
            <Input
              value={draft.title}
              placeholder={
                draft.titleType === InvoiceTitleType.COMPANY ? '请输入单位全称' : '请输入姓名'
              }
              onChange={(value) => patch({ title: value })}
            />
          </span>
        </div>

        {draft.titleType === InvoiceTitleType.COMPANY ? (
          <div className="field">
            <span className="field__label">
              纳税人识别号<em className="field__required">*</em>
            </span>
            <span className="field__control">
              <Input
                value={draft.taxNo}
                placeholder="请输入纳税人识别号"
                onChange={(value) => patch({ taxNo: value })}
              />
            </span>
          </div>
        ) : null}

        <div className="field">
          <span className="field__label">备注</span>
          <span className="field__control">
            <Input
              value={draft.remark}
              placeholder="选填：收票邮箱 / 特殊要求"
              onChange={(value) => patch({ remark: value })}
            />
          </span>
        </div>
      </Card>

      <Notice tone="info">
        开票金额由系统按实缴金额填写，无需手工输入；提交后由财务人工开具。
      </Notice>

      {error ? <Notice tone="danger">{error}</Notice> : null}
      {problem ? <div className="invoice-panel__hint">{problem}</div> : null}

      <div className="btn-row">
        {onCancel ? (
          <Button variant="ghost" disabled={submitting} onClick={onCancel}>
            取消
          </Button>
        ) : null}
        <Button variant="primary" loading={submitting} disabled={problem !== null} onClick={submit}>
          提交申请
        </Button>
      </div>
    </>
  );
}

export function InvoicePanel({ registrationId, canApply, applicantName }: InvoicePanelProps) {
  const invoice = useMyInvoice(registrationId);
  /** 未申请时是否展开表单；驳回态下点「重新申请」也会置为 true */
  const [editing, setEditing] = useState(false);

  if (invoice.isPending) return <LoadingBlock text="正在加载开票信息" />;

  if (invoice.error) {
    return (
      <>
        <Notice tone="warning">开票信息暂时取不到，可稍后重试。</Notice>
        <Button
          variant="ghost"
          block
          onClick={() => {
            void invoice.refetch();
          }}
        >
          重新加载开票信息
        </Button>
      </>
    );
  }

  const record = invoice.data ?? null;
  const refresh = async () => {
    await invoice.refetch();
    setEditing(false);
  };

  // 已有生效中的申请（待开具 / 已开具）：不给「再申请一个」的入口 —— 服务端也会以 INVOICE-002 拒绝
  if (record && record.status !== InvoiceStatus.REJECTED) {
    return (
      <>
        <Notice tone="info">
          {record.status === InvoiceStatus.REQUESTED
            ? '开票申请已提交，财务开具后可在本页查看发票号码。'
            : '发票已开具，请核对下方发票号码与抬头。'}
        </Notice>
        <InvoiceDetail record={record} />
      </>
    );
  }

  // 已驳回：先给原因 + 上次填的内容，再给「重新申请」（驳回不是终态）
  if (record && record.status === InvoiceStatus.REJECTED && !editing) {
    return (
      <>
        <Notice tone="danger">
          本次开票申请未通过：{record.rejectReason ?? '未说明原因'}。请修改后重新提交。
        </Notice>
        <InvoiceDetail record={record} />
        <Button variant="primary" block onClick={() => setEditing(true)}>
          重新申请
        </Button>
      </>
    );
  }

  if (editing) {
    return (
      <InvoiceForm
        registrationId={registrationId}
        applicantName={applicantName}
        onApplied={refresh}
        onCancel={record ? () => setEditing(false) : undefined}
      />
    );
  }

  if (!canApply) {
    return (
      <Card title="发票" tight>
        <KV label="开票入口" value="报名成功后即可在此申请开票" soft />
      </Card>
    );
  }

  return (
    <Card title="发票" tight>
      <Cell
        title="需要发票？"
        description="报名成功后按实缴金额开具"
        arrow
        onClick={() => setEditing(true)}
      />
    </Card>
  );
}
